import React, { useEffect, useState } from 'react';
import LoadingSpinner from 'component/LoadingSpinner';
import ProjectUserService from 'service/ClientTransmoraService/ProjectUserService';
import { ProjectUserDetailResp } from 'domain/payload/response/clientTransmora/projectUser/ProjectUserDetailResp';

interface ProjectUserDetailModalProps {
  show: boolean;
  onClose: () => void;
  userId: string | null;
  token?: string;
}

const ProjectUserDetailModal: React.FC<ProjectUserDetailModalProps> = ({
  show,
  onClose,
  userId,
  token,
}) => {
  const [detail, setDetail] = useState<ProjectUserDetailResp | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const projectUserService = ProjectUserService();

  useEffect(() => {
    if (show && userId && token) {
      setLoading(true);
      setError(null);
      projectUserService
        .projectUserDetail(token, userId)
        .then((res) => {
          setDetail(res.data ?? null);
        })
        .catch((err: any) => {
          console.error('Error fetching project user detail:', err);
          const msg = typeof err === 'string' ? err : err?.response?.data?.message || err?.message || 'Failed to load details';
          setError(msg);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setDetail(null);
      setError(null);
    }
  }, [show, userId, token]);

  if (!show) return null;

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      role="dialog"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 1060,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        style={{ maxWidth: '550px', width: '100%' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-0 shadow modal-content" style={{ borderRadius: '12px' }}>
          <div className="modal-header border-bottom-0 pb-0">
            <h5 className="modal-title fw-bold">Client Project User Details</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body px-4 py-3">
            {loading ? (
              <div className="py-5 text-center">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            ) : detail ? (
              <div className="row gy-3">
                <div className="col-12">
                  <div className="p-3 rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Status</span>
                      <span className={`badge ${detail.active ? 'bg-success' : 'bg-secondary'}`}>
                        {detail.status || (detail.active ? 'Active' : 'Inactive')}
                      </span>
                    </div>
                    <h5 className="mb-1 fw-bold text-primary">{detail.name}</h5>
                    <p className="mb-0 text-muted small">{detail.email}</p>
                  </div>
                </div>

                <div className="col-6">
                  <label className="text-muted small mb-1 fw-semibold">User ID</label>
                  <p className="mb-0 font-monospace small text-break">{detail.id}</p>
                </div>

                <div className="col-6">
                  <label className="text-muted small mb-1 fw-semibold">Phone</label>
                  <p className="mb-0">{detail.phone || '-'}</p>
                </div>

                <div className="col-6">
                  <label className="text-muted small mb-1 fw-semibold">Client Name</label>
                  <p className="mb-0 fw-semibold">{detail.clientName || detail.client || '-'}</p>
                </div>

                <div className="col-6">
                  <label className="text-muted small mb-1 fw-semibold">Created At</label>
                  <p className="mb-0">{detail.createdAt || '-'}</p>
                </div>

                <div className="col-12">
                  <label className="text-muted small mb-1 fw-semibold">Roles</label>
                  <div className="d-flex flex-wrap gap-1 mt-1">
                    {detail.roles && detail.roles.length > 0 ? (
                      detail.roles.map((r) => (
                        <span key={r.id} className="badge bg-primary-subtle text-primary border border-primary-subtle">
                          {r.name}
                        </span>
                      ))
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          <div className="modal-footer border-top-0 pt-0">
            <button type="button" className="btn btn-secondary px-4" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectUserDetailModal;
